import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from '@/hooks/useTranslation';
import AuthLayout from '@/layouts/auth-layout';
import { Head, Link } from '@inertiajs/react';
import { ArrowLeft, ScrollText } from 'lucide-react';

interface LegalDocument {
    key: string;
    title: string;
    version: string;
    content: string;
    effective_at: string | null;
}

interface Props {
    document: LegalDocument;
    links: {
        back: string | null;
        terms: string;
        privacy: string;
    };
}

export default function Document({ document, links }: Props) {
    const { t } = useTranslation('legal');
    const isTerms = document.key === 'terms';

    return (
        <AuthLayout pageClassName="p-4 sm:p-6 md:p-10" contentClassName="max-w-5xl" logoClassName="h-24 w-24 sm:h-32 sm:w-32">
            <Head title={document.title} />

            <Card className="mx-auto w-full">
                <CardHeader className="gap-5 px-5 sm:px-8">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-start gap-3">
                            <div className="rounded-md bg-primary/10 p-2 text-primary">
                                <ScrollText className="h-5 w-5" />
                            </div>
                            <div className="space-y-2">
                                <CardTitle className="text-2xl leading-8">{document.title}</CardTitle>
                                <CardDescription className="text-sm leading-6">
                                    {t('version')} {document.version}
                                    {document.effective_at ? ` - ${t('effective_from')} ${document.effective_at}` : ''}
                                </CardDescription>
                            </div>
                        </div>

                        {links.back && (
                            <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                                <Link href={links.back}>
                                    <ArrowLeft className="h-4 w-4" />
                                    {t('back_to_acknowledgement')}
                                </Link>
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-7 px-5 sm:px-8">
                    <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row">
                        <Button asChild variant={isTerms ? 'default' : 'outline'} className="flex-1">
                            <Link href={links.terms}>{t('terms')}</Link>
                        </Button>
                        <Button asChild variant={!isTerms ? 'default' : 'outline'} className="flex-1">
                            <Link href={links.privacy}>{t('privacy_notice')}</Link>
                        </Button>
                    </div>

                    <Separator />

                    <article className="mx-auto max-w-3xl text-base leading-8 whitespace-pre-line text-foreground/85">{document.content}</article>

                    {links.back && (
                        <>
                            <Separator />
                            <div className="flex justify-start">
                                <Button asChild variant="outline">
                                    <Link href={links.back}>
                                        <ArrowLeft className="h-4 w-4" />
                                        {t('back_to_acknowledgement')}
                                    </Link>
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </AuthLayout>
    );
}
